import { useRef, useState, useEffect, useCallback } from 'react';
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from 'framer-motion';
import {
  FiEye,
  FiTarget,
  FiAward,
  FiZap,
  FiShield,
  FiHeart,
  FiUsers,
  FiFeather,
  FiArrowRight,
  FiMapPin,
  FiCalendar,
  FiCheckCircle,
  FiStar,
  FiUser,
} from 'react-icons/fi';
import SplitText from '../components/SplitText';

/* ═══════════════════════════
   DESIGN TOKENS
═══════════════════════════ */
const T = {
  red:      '#C8102E',
  redLight: '#FEF0F2',
  redMid:   'rgba(200,16,46,0.10)',
  redGlow:  'rgba(200,16,46,0.06)',
  ink:      '#1C1C1E',
  inkSoft:  '#3A3A3C',
  ash:      '#636366',
  silver:   '#AEAEB2',
  border:   '#E5E5EA',
  offWhite: '#F9F9FB',
  cream:    '#FFFFFF',
  white:    '#FFFFFF',
  cardBg:   '#FAFAFA',
};

const EASE        = [0.22, 1, 0.36, 1];
const EASE_IN_OUT = [0.76, 0, 0.24, 1];

/* ═══════════════════════════
   MAGNETIC HOOK
═══════════════════════════ */
function useMagnetic(strength = 0.18) {
  const ref  = useRef(null);
  const x    = useMotionValue(0);
  const y    = useMotionValue(0);
  const sx   = useSpring(x, { stiffness: 180, damping: 20 });
  const sy   = useSpring(y, { stiffness: 180, damping: 20 });
  const onMove = useCallback((e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width  / 2) * strength);
    y.set((e.clientY - r.top  - r.height / 2) * strength);
  }, [x, y, strength]);
  const onLeave = useCallback(() => { x.set(0); y.set(0); }, [x, y]);
  return { ref, x: sx, y: sy, onMove, onLeave };
}

/* ═══════════════════════════
   SCROLL REVEAL
═══════════════════════════ */
const Reveal = ({ children, delay = 0, direction = 'up', style = {}, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const map = { up: [0, 40], down: [0, -40], left: [40, 0], right: [-40, 0] };
  const [ox, oy] = map[direction] || [0, 40];
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={{ opacity: 0, x: ox, y: oy }}
      animate={inView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

/* ═══════════════════════════
   SECTION LABEL
═══════════════════════════ */
const Label = ({ children, center = false }) => (
  <Reveal>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      fontSize: '10px', letterSpacing: '3px', textTransform: 'uppercase',
      color: T.red, marginBottom: '16px', fontWeight: 700,
      justifyContent: center ? 'center' : 'flex-start',
    }}>
      <motion.span
        initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
        transition={{ duration: 0.5, ease: EASE }}
        style={{
          display: 'block', width: '20px', height: '2px',
          background: T.red, transformOrigin: 'left', flexShrink: 0,
          borderRadius: '2px',
        }}
      />
      {children}
      {center && (
        <motion.span
          initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.1 }}
          style={{
            display: 'block', width: '20px', height: '2px',
            background: T.red, transformOrigin: 'right', flexShrink: 0,
            borderRadius: '2px',
          }}
        />
      )}
    </div>
  </Reveal>
);

/* ═══════════════════════════
   SECTION HEADING
═══════════════════════════ */
const SectionHeading = ({ children, center = false, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <div ref={ref} style={{ textAlign: center ? 'center' : 'left' }}>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, delay, ease: EASE }}
        style={{
          margin: 0,
          fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)',
          fontWeight: 800,
          color: T.ink,
          letterSpacing: '-0.5px',
          lineHeight: 1.2,
        }}
      >
        {children}
      </motion.h2>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={inView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.6, delay: delay + 0.25, ease: EASE }}
        style={{
          height: '3px',
          background: `linear-gradient(90deg, ${T.red}, transparent)`,
          marginTop: '12px',
          transformOrigin: center ? 'center' : 'left',
          width: center ? '72px' : '100%',
          maxWidth: center ? '72px' : 'none',
          margin: center ? '12px auto 0' : '12px 0 0',
          borderRadius: '2px',
        }}
      />
    </div>
  );
};

/* ═══════════════════════════
   STAT COUNTER
═══════════════════════════ */
const CountUp = ({ value, suffix = '', label }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView || isNaN(parseFloat(value))) { setN(value); return; }
    const target = parseFloat(value);
    let frame = 0;
    const total = 72;
    const timer = setInterval(() => {
      frame++;
      const ease = 1 - Math.pow(1 - frame / total, 4);
      setN(Math.round(ease * target));
      if (frame >= total) { setN(target); clearInterval(timer); }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);
  return (
    <div ref={ref} style={{ textAlign: 'center', padding: '8px' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.6, ease: EASE }}
        style={{
          fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
          fontWeight: 900, color: T.red, lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-1px',
        }}
      >
        {n}{suffix}
      </motion.div>
      <div style={{
        fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
        color: T.ash, marginTop: '10px', fontWeight: 600,
      }}>
        {label}
      </div>
    </div>
  );
};

/* ═══════════════════════════
   FACT CHIP
═══════════════════════════ */
const FactChip = ({ icon: Icon, highlight, label, delay }) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: EASE }}
      whileHover={{ y: -4 }}
      style={{
        background: T.white,
        border: `1px solid ${hovered ? 'rgba(200,16,46,0.22)' : T.border}`,
        borderRadius: '8px',
        padding: '18px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        cursor: 'default',
        boxShadow: hovered
          ? '0 14px 36px rgba(200,16,46,0.12), 0 2px 8px rgba(0,0,0,0.04)'
          : '0 2px 12px rgba(0,0,0,0.05)',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <motion.div
        animate={{ scaleX: hovered ? 1 : 0 }}
        initial={{ scaleX: 0 }}
        transition={{ duration: 0.3, ease: EASE }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: T.red,
          transformOrigin: 'left',
        }}
      />
      <motion.div
        animate={{
          background: hovered ? T.red : T.redLight,
          color: hovered ? T.white : T.red,
        }}
        transition={{ duration: 0.3 }}
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={19} strokeWidth={2} />
      </motion.div>
      <div style={{ textAlign: 'left', minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: '15px',
          fontWeight: 800,
          color: T.ink,
          letterSpacing: '-0.3px',
          lineHeight: 1.25,
        }}>
          {highlight}
        </p>
        <p style={{
          margin: '4px 0 0',
          fontSize: '12px',
          fontWeight: 500,
          color: T.ash,
          lineHeight: 1.3,
        }}>
          {label}
        </p>
      </div>
    </motion.div>
  );
};

/* ═══════════════════════════
   SPEC / WHAT WE DO ITEM
═══════════════════════════ */
const SpecItem = ({ title, subtitle, index }) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -24 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.65, delay: index * 0.07, ease: EASE }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '22px 20px',
        cursor: 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: hovered ? '6px' : '0',
        transition: 'border-radius 0.35s ease',
      }}
    >
      <motion.div
        animate={{ scaleX: hovered ? 1 : 0 }}
        initial={{ scaleX: 0 }}
        transition={{ duration: 0.35, ease: EASE_IN_OUT }}
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${T.red} 0%, #E8384F 100%)`,
          transformOrigin: 'left',
          zIndex: 0,
          borderRadius: hovered ? '6px' : '0',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '18px' }}>
        <motion.span
          animate={{ color: hovered ? 'rgba(255,255,255,0.45)' : T.silver }}
          style={{ fontSize: '11px', letterSpacing: '2px', fontWeight: 700, minWidth: '24px' }}
        >
          {String(index + 1).padStart(2, '0')}
        </motion.span>
        <div>
          <motion.div
            animate={{ color: hovered ? T.white : T.ink }}
            style={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '0.1px' }}
          >
            {title}
          </motion.div>
          <motion.div
            animate={{ color: hovered ? 'rgba(255,255,255,0.65)' : T.ash }}
            style={{ fontSize: '13px', marginTop: '3px' }}
          >
            {subtitle}
          </motion.div>
        </div>
      </div>
      <motion.div
        animate={{ x: hovered ? 0 : 12, opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ position: 'relative', zIndex: 1, color: T.white, fontSize: '1.2rem' }}
      >
        <FiArrowRight />
      </motion.div>
    </motion.div>
  );
};

/* ═══════════════════════════
   VISION / MISSION / PROMISE CARD
═══════════════════════════ */
const VisionCard = ({ iconKey, label, text, delay }) => {
  const [hovered, setHovered] = useState(false);

  const ICONS = {
    vision:  <FiEye size={24} />,
    mission: <FiTarget size={24} />,
    promise: <FiAward size={24} />,
  };

  return (
    <Reveal delay={delay} direction="up">
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        animate={{
          y: hovered ? -6 : 0,
          boxShadow: hovered
            ? '0 20px 48px rgba(200,16,46,0.14), 0 2px 8px rgba(0,0,0,0.06)'
            : '0 2px 12px rgba(0,0,0,0.06)',
        }}
        transition={{ duration: 0.3, ease: EASE }}
        style={{
          background: T.white,
          borderRadius: '8px',
          padding: '32px 28px 28px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '14px',
          cursor: 'default',
          border: `1px solid ${T.border}`,
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <motion.div
          animate={{ scaleX: hovered ? 1 : 0 }}
          initial={{ scaleX: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '3px',
            background: T.red,
            transformOrigin: 'left',
          }}
        />
        <motion.div
          animate={{
            background: hovered ? T.red : T.redLight,
            color: hovered ? T.white : T.red,
          }}
          transition={{ duration: 0.3 }}
          style={{
            width: '48px', height: '48px',
            borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {ICONS[iconKey]}
        </motion.div>
        <p style={{
          margin: 0,
          fontWeight: 800,
          fontSize: '15px',
          color: T.ink,
          letterSpacing: '-0.2px',
          lineHeight: 1.3,
        }}>
          {label}
        </p>
        <p style={{
          margin: 0,
          fontSize: '13.5px',
          color: T.ash,
          lineHeight: '1.75',
        }}>
          {text}
        </p>
      </motion.div>
    </Reveal>
  );
};

/* ═══════════════════════════
   CORE VALUES DATA
═══════════════════════════ */
const CORE_VALUES = [
  {
    icon: (
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    label: 'Leadership',
    desc: 'Lead by example — setting the standard in quality, ethics, and commitment on every project.',
  },
  {
    icon: (
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    label: 'Trust',
    desc: 'Build trust together — earned through transparency, consistency, and keeping every promise.',
  },
  {
    icon: (
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    label: 'Integrity',
    desc: 'Do right things — maintaining honesty and strong moral principles in every decision we make.',
  },
  {
    icon: (
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
    label: 'Passion',
    desc: 'Work with excitement — channeling genuine enthusiasm into every label, decal, and graphic we create.',
  },
  {
    icon: (
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
      </svg>
    ),
    label: 'Creativity',
    desc: 'Turn ideas into reality — bringing bold, imaginative thinking to every brief and solution.',
  },
  {
    icon: (
      <svg width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2"/>
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        <line x1="12" y1="12" x2="12" y2="16"/>
        <line x1="10" y1="14" x2="14" y2="14"/>
      </svg>
    ),
    label: 'Team Work',
    desc: 'Play for each other — collaborating across every department so the whole is greater than the sum.',
  },
];

/* ═══════════════════════════
   VALUE CARD
═══════════════════════════ */
const ValueCard = ({ icon, label, desc, delay }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Reveal delay={delay} direction="up" style={{ height: '100%' }}>
      <div style={{ height: '100%' }}>
        <motion.div
          onHoverStart={() => setHovered(true)}
          onHoverEnd={() => setHovered(false)}
          animate={{
            y: hovered ? -4 : 0,
            boxShadow: hovered
              ? '0 16px 40px rgba(204,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)'
              : '0 2px 14px rgba(0,0,0,0.06)',
          }}
          transition={{ duration: 0.3, ease: EASE }}
          style={{
            height: '100%',
            minHeight: '190px',
            background: '#ffffff',
            borderRadius: '4px',
            padding: '30px 28px 28px',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'default',
            border: '1px solid #e8e8e8',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'left',
          }}
        >
          <motion.div
            animate={{ scaleX: hovered ? 1 : 0 }}
            initial={{ scaleX: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: T.red,
              transformOrigin: 'left',
              zIndex: 2,
            }}
          />

          <motion.div
            animate={{ opacity: hovered ? 0.14 : 0.07, color: hovered ? T.red : '#999' }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              right: '-36px',
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
              lineHeight: 0,
              zIndex: 0,
            }}
          >
            {icon}
          </motion.div>

          <div style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: '10px',
            maxWidth: '78%',
          }}>
            <p style={{
              margin: 0,
              fontWeight: 700,
              fontSize: '16px',
              color: '#1a1a1a',
              letterSpacing: '-0.2px',
              lineHeight: 1.35,
              minHeight: '48px',
              display: 'flex',
              alignItems: 'flex-start',
            }}>
              {label}
            </p>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#666',
              lineHeight: 1.65,
              flex: 1,
            }}>
              {desc}
            </p>
          </div>
        </motion.div>
      </div>
    </Reveal>
  );
};

/* ═══════════════════════════
   LEADERSHIP DATA
═══════════════════════════ */
const LEADERS = [
  {
    name: 'Shailesh Deshpande',
    title: 'Managing Director',
    image: '/leadership/Shailesh Sir.jpg',
  },
  {
    name: 'Shweta Deshpande',
    title: 'Director – Finance',
    image: '/leadership/Shweta Mam.jpeg',
  },
  {
    name: 'Shekhar Surve',
    title: 'Director – Operations',
    image: '/leadership/shekhar surve sir.jpeg',
  },
  {
    name: 'Abhishek Deo',
    title: 'Operations Manager',
    image: '/leadership/Abhishek.jpeg',
  },
];

/* ═══════════════════════════
   LEADER CARD
═══════════════════════════ */
const LeaderCard = ({ name, title, image, delay }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Reveal delay={delay} direction="up" style={{ height: '100%' }}>
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        animate={{
          y: hovered ? -4 : 0,
          boxShadow: hovered
            ? '0 16px 40px rgba(200,16,46,0.14), 0 2px 8px rgba(0,0,0,0.06)'
            : '0 2px 14px rgba(0,0,0,0.06)',
        }}
        transition={{ duration: 0.3, ease: EASE }}
        style={{
          height: '100%',
          background: T.white,
          borderRadius: '8px',
          border: `1px solid ${hovered ? 'rgba(200,16,46,0.2)' : T.border}`,
          overflow: 'hidden',
          cursor: 'default',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <motion.div
          animate={{ scaleX: hovered ? 1 : 0 }}
          initial={{ scaleX: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: T.red,
            transformOrigin: 'left',
            zIndex: 2,
          }}
        />

        <div style={{
          width: '100%',
          aspectRatio: '4 / 5',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(165deg, ${T.offWhite} 0%, #ECECEF 100%)`,
          flexShrink: 0,
        }}>
          {image ? (
            <img
              src={image}
              alt={name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
                display: 'block',
              }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="88" height="88" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.16 }}>
                <circle cx="12" cy="8" r="4" fill={T.ink} />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill={T.ink} />
              </svg>
            </div>
          )}
        </div>

        <div style={{
          padding: '18px 16px 22px',
          textAlign: 'center',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '6px',
        }}>
          <motion.p
            animate={{ color: hovered ? T.red : T.ink }}
            transition={{ duration: 0.25 }}
            style={{
              margin: 0,
              fontSize: '17px',
              fontWeight: 700,
              letterSpacing: '-0.2px',
              lineHeight: 1.3,
            }}
          >
            {name}
          </motion.p>
          <p style={{
            margin: 0,
            fontSize: '13px',
            fontWeight: 500,
            color: T.ash,
            lineHeight: 1.45,
          }}>
            {title}
          </p>
        </div>
      </motion.div>
    </Reveal>
  );
};

/* ═══════════════════════════
   DATA
═══════════════════════════ */
const WHAT_WE_DO = [
  { title: 'Self-Adhesive Labels',  subtitle: 'Multi-substrate labels for packaging, product identification, and logistics' },
  { title: 'Automotive Graphics',   subtitle: 'Body decals, stripes, and livery kits for OEMs & aftermarket' },
  { title: 'Helmet Decals',         subtitle: 'Water-slide transfers for helmets with vivid colour depth' },
  { title: 'PU Domed Labels',       subtitle: '3D polyurethane domed badges for a premium tactile finish' },
  { title: 'Vehicle Wrap Films',    subtitle: 'Full-vehicle colour-change and protective films' },
  { title: 'Reflective Labels',     subtitle: 'High-visibility safety & compliance marking solutions' },
];

/* ═══════════════════════════
   SECTION WRAPPER
═══════════════════════════ */
const SectionWrap = ({ children, bg = T.white, style = {} }) => (
  <section style={{ padding: '100px 8%', background: bg, ...style }}>
    <div style={{ maxWidth: '1160px', margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

/* ═══════════════════════════
   WORD REVEAL
═══════════════════════════ */
const WordReveal = ({ text, style = {}, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <div ref={ref} style={{ display: 'flex', flexWrap: 'wrap', gap: '0 8px', ...style }}>
      {text.split(' ').map((word, i) => (
        <div key={i} style={{ overflow: 'hidden', lineHeight: 1.25 }}>
          <motion.span
            initial={{ y: '110%', opacity: 0 }}
            animate={inView ? { y: '0%', opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: delay + i * 0.05, ease: EASE }}
            style={{ display: 'block' }}
          >
            {word}
          </motion.span>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════
   MAIN COMPONENT
═══════════════════════════ */
const AboutUsscreen = () => {
  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY       = useTransform(heroP, [0, 1], ['0%', '14%']);
  const heroOpacity = useTransform(heroP, [0, 0.75], [1, 0]);

  return (
    <div style={{
      background: T.white,
      overflowX: 'hidden',
      position: 'relative',
      fontFamily: "'Inter', sans-serif",
    }}>

      {/* ══ HERO ══ */}
      <motion.section
        ref={heroRef}
        style={{
          y: heroY, opacity: heroOpacity,
          minHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          textAlign: 'center',
          padding: '120px 8% 80px',
          position: 'relative',
          background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${T.redGlow} 0%, transparent 70%)`,
        }}
      >
        {/* Grid lines */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, opacity: 0.025 }}>
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 1.2, delay: i * 0.06, ease: EASE }}
              style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${(i + 1) * 11}%`, width: '1px',
                background: T.ink, transformOrigin: 'top',
              }}
            />
          ))}
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '840px', marginTop: '-70px', marginBottom: '-20px' }}>
          <div style={{ marginBottom: '4px' }}>
            <SplitText text="About Us" tag="h1" duration={1.5} delay={80} splitType="chars" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.65, ease: EASE }}
            style={{ maxWidth: '720px', margin: '28px auto 0' }}
          >
            <p style={{ fontSize: '16px', lineHeight: '1.9', color: T.ash, marginBottom: '16px' }}>
              Established in 2017, Decal Tech Private Limited is one of the leading manufacturers of
              automotive graphics, helmet decals, display graphics, & brand identification labels.
              Decal Tech, an ISO 9001:2015 certified company, has its state of the art manufacturing
              facility at Thane - Maharashtra and it spreads over an area of approximately 6000 sq. ft.
              High performance team with a blend of seasoned professionals and young enthusiasts
              works with a passion and turn creative ideas into reality to create customer value.
              Esteemed list of customers include industry giants like Ashok Leyland, Godrej Boyce,
              Tata AutoComp, Vega Auto, Studds, Bharat Bijlee, Protech Sports & Safety products.
            </p>
          </motion.div>

          {/* Chips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05, ease: EASE }}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))',
              gap: '14px',
              marginTop: '40px',
              maxWidth: '820px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {[
              { icon: FiMapPin,      highlight: '7,500 sq. ft.', label: 'Manufacturing facility' },
              { icon: FiCheckCircle, highlight: 'ISO 9001:2015', label: 'Quality certified'      },
              { icon: FiHeart,       highlight: '500+',          label: 'Happy clients'          },
              { icon: FiCalendar,    highlight: '7 years',       label: 'Of excellence'          },
            ].map((c, i) => (
              <FactChip key={i} icon={c.icon} highlight={c.highlight} label={c.label} delay={1.15 + i * 0.07} />
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* ══ WHAT WE DO ══ */}
      <SectionWrap bg={T.white}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <SectionHeading center delay={0.1}>Our Specialisations</SectionHeading>
          <Reveal delay={0.25}>
            <p style={{ fontSize: '15px', color: T.ash, lineHeight: '1.82', marginTop: '18px', maxWidth: '560px', margin: '18px auto 0' }}>
              Six product lines, one commitment — every item that leaves our floor meets exact specification.
            </p>
          </Reveal>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '20px 40px',
        }}>
          {WHAT_WE_DO.map((item, i) => (
            <SpecItem key={i} {...item} index={i} />
          ))}
        </div>
      </SectionWrap>

      {/* ══ VISION, MISSION & PROMISE ══ */}
      <SectionWrap bg={T.offWhite}>
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <SectionHeading center delay={0.1}>Vision & Mission</SectionHeading>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '28px',
          alignItems: 'stretch',
        }}>
          <VisionCard
            iconKey="vision"
            label="Our Vision"
            text="To be India's most trusted name in precision labelling and decal manufacturing — known for products that perform under the toughest conditions, from high-speed assembly lines to extreme outdoor environments."
            delay={0}
          />
          <VisionCard
            iconKey="mission"
            label="Our Mission"
            text="To deliver innovative, durable, and customised branding solutions through advanced manufacturing, rigorous quality control, and a genuine commitment to every client's success."
            delay={0.1}
          />
          <VisionCard
            iconKey="promise"
            label="Our Promise"
            text="Every product that leaves our facility meets the exact specification agreed upon — backed by in-house lab testing, transparent communication, and reliable on-time delivery."
            delay={0.2}
          />
        </div>
      </SectionWrap>

      {/* ══ CORE VALUES ══ */}
      <SectionWrap bg={T.white}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <SectionHeading center delay={0.1}>Core Values</SectionHeading>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
          gap: '18px',
          maxWidth: '1140px',
          margin: '0 auto',
          alignItems: 'stretch',
          marginBottom: '10px',
        }}>
          {CORE_VALUES.map((item, i) => (
            <ValueCard
              key={item.label}
              icon={item.icon}
              label={item.label}
              desc={item.desc}
              delay={i * 0.08}
            />
          ))}
        </div>
      </SectionWrap>

      {/* ══ LEADERSHIP ══ */}
      <SectionWrap bg={T.offWhite} style={{ padding: '72px 6%' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <SectionHeading center delay={0.1}>Our Leadership</SectionHeading>
          <Reveal delay={0.25}>
            <p style={{
              fontSize: '15px', color: T.ash, lineHeight: '1.82',
              marginTop: '16px', maxWidth: '520px', margin: '16px auto 0',
            }}>
              The people behind Decal Tech's vision — driving excellence every day.
            </p>
          </Reveal>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '18px',
          maxWidth: '1140px',
          margin: '0 auto',
          alignItems: 'stretch',
        }}>
          {LEADERS.map((leader, i) => (
            <LeaderCard
              key={i}
              name={leader.name}
              title={leader.title}
              image={leader.image}
              delay={i * 0.1}
            />
          ))}
        </div>
      </SectionWrap>

    </div>
  );
};

export default AboutUsscreen;